import React, { useEffect, useRef } from 'react';

type UseDraggableOptions = {
  isEnabled: boolean;
};

export function useDraggable<T extends HTMLElement>({ isEnabled }: UseDraggableOptions) {
  const offsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const handleRef = useRef<T>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isEnabled) {
      offsetRef.current = { x: 0, y: 0 };
      if (targetRef.current) {
        targetRef.current.style.left = '0px';
        targetRef.current.style.top = '0px';
      }
    }
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled) return;

    const handleResize = () => {
      if (!targetRef.current) return;
      const { x, y } = clampOffset(offsetRef.current.x, offsetRef.current.y);
      offsetRef.current = { x, y };
      targetRef.current.style.left = `${x}px`;
      targetRef.current.style.top = `${y}px`;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isEnabled]);

  const clampOffset = (nextX: number, nextY: number) => {
    const handle = handleRef.current;
    if (!handle) return { x: nextX, y: nextY };

    const rect = handle.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const baseLeft = rect.left - offsetRef.current.x;
    const baseTop = rect.top - offsetRef.current.y;

    const nextLeft = baseLeft + nextX;
    const nextTop = baseTop + nextY;
    const nextCenterX = nextLeft + rect.width / 2;

    let clampedX = nextX;
    let clampedY = nextY;

    if (nextCenterX < 0) clampedX = -rect.width / 2 - baseLeft;
    if (nextCenterX > viewportWidth) clampedX = viewportWidth - rect.width / 2 - baseLeft;

    if (nextTop < 0) clampedY = -baseTop;
    if (nextTop + rect.height > viewportHeight) clampedY = viewportHeight - rect.height - baseTop;

    return { x: clampedX, y: clampedY };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    dragStartPos.current = {
      x: e.clientX - offsetRef.current.x,
      y: e.clientY - offsetRef.current.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current || !targetRef.current) return;
    const { x, y } = clampOffset(
      e.clientX - dragStartPos.current.x,
      e.clientY - dragStartPos.current.y,
    );
    offsetRef.current = { x, y };
    targetRef.current.style.left = `${x}px`;
    targetRef.current.style.top = `${y}px`;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    isDraggingRef.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 50 : 10;
    let nextX = offsetRef.current.x;
    let nextY = offsetRef.current.y;

    switch (e.key) {
      case 'ArrowUp':
        nextY -= step;
        break;
      case 'ArrowDown':
        nextY += step;
        break;
      case 'ArrowLeft':
        nextX -= step;
        break;
      case 'ArrowRight':
        nextX += step;
        break;
      default:
        return;
    }

    const { x, y } = clampOffset(nextX, nextY);
    offsetRef.current = { x, y };
    if (targetRef.current) {
      targetRef.current.style.left = `${x}px`;
      targetRef.current.style.top = `${y}px`;
    }
  };

  return {
    handleRef,
    targetRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onKeyDown,
  };
}
